import React from "react";

/**
 * SourceBadge (SOUND-293)
 * -------------------------------------------------------
 * Unified renderer for the `source` field returned by the
 * /companies, /applications, /mids, /mid-applications endpoints.
 *
 * Shape (from SOUND-291):
 *   source: {
 *     type: "partner" | "self-serve" | "internal",
 *     partners?: [{ partnerId, partnerName, role }]
 *   }
 *
 * Rendering aligns with the SourceCell that SOUND-292 added
 * inline in Companies.jsx — we mirror it here so every list
 * and detail surface looks identical.
 *
 * Rules:
 *   - partner: comma-separated partner names; role pill when
 *     role !== "primary"
 *   - self-serve: plain "Self-serve" label
 *   - internal: plain "Internal" label
 *   - missing: em-dash placeholder
 */
function cx(...cls) {
  return cls.filter(Boolean).join(" ");
}

export default function SourceBadge({ source, className, placeholder = "—" }) {
  if (!source || !source.type) {
    return <span className={cx("text-sm text-muted-foreground", className)}>{placeholder}</span>;
  }

  if (source.type === "self-serve") {
    return <span className={cx("text-sm text-foreground", className)}>Self-serve</span>;
  }

  if (source.type === "internal") {
    return <span className={cx("text-sm text-foreground", className)}>Internal</span>;
  }

  if (source.type === "partner") {
    const partners = Array.isArray(source.partners) ? source.partners : [];
    if (partners.length === 0) {
      return <span className={cx("text-sm text-foreground", className)}>Partner</span>;
    }
    return (
      <div className={cx("flex flex-wrap items-center gap-x-2 gap-y-1", className)}>
        {partners.map((p, idx) => (
          <span
            key={p.partnerId || `${p.partnerName}-${idx}`}
            className="inline-flex items-center gap-1 text-sm text-foreground"
          >
            <span className="truncate max-w-[200px]">{p.partnerName || "Unknown"}</span>
            {p.role && p.role !== "primary" && (
              <span className="badge badge-muted text-[10px] uppercase">
                {p.role}
              </span>
            )}
            {idx < partners.length - 1 && (
              <span className="text-muted-foreground">,</span>
            )}
          </span>
        ))}
      </div>
    );
  }

  return <span className={cx("text-sm text-foreground", className)}>{source.type}</span>;
}
